import { type KeyboardEvent, useCallback } from 'react'

import { useI18N } from '@susu/i18n/client'

import { useToast } from '../contexts/ToastContext'
import { copyToClipboard } from '../utils/clipboard'

type ClickableProps =
  | Record<string, never>
  | {
      role: 'button'
      tabIndex: number
      onClick: () => void
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
      'aria-label': string
    }

interface UseCopyProductCodeResult {
  /** Whether there is a code to copy (drives clickability + cursor). */
  canCopy: boolean
  /** The normalised (uppercased) product code that gets copied. */
  productCode: string
  /** Spread onto the card element to make it a copy button, or `{}` when empty. */
  clickableProps: ClickableProps
}

/**
 * Shared click/keyboard "copy product code" behaviour for customer hub item
 * cards. Copies the code to the clipboard and shows a confirmation toast, only
 * surfacing the toast when the write actually succeeds.
 */
export function useCopyProductCode(rawCode: string): UseCopyProductCodeResult {
  const { showToast } = useToast()
  const { t } = useI18N()
  const productCode = rawCode.toUpperCase()
  const canCopy = Boolean(productCode)

  const handleCopyProductCode = useCallback(async () => {
    if (!canCopy) return
    const copied = await copyToClipboard(productCode)
    if (copied) {
      showToast(t('toasts.copiedProductCode'))
    }
  }, [canCopy, productCode, showToast, t])

  const clickableProps: ClickableProps = canCopy
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: handleCopyProductCode,
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            void handleCopyProductCode()
          }
        },
        'aria-label': `Copy product code ${productCode}`,
      }
    : {}

  return { canCopy, productCode, clickableProps }
}
